import PropTypes from 'prop-types'

import { pv } from '../motion'
import { COLORS, Shape } from '../primitives'
import { inB } from './beats'
import { Desk, Heart, INK, Person, Plant } from './figures'
import { IsoBox, IsoRect, iso, pts } from './iso'

/**
 * Das statische Miniatur-Office: Boden, Wände, Möbel und alle Personen, die
 * an ihrem Platz bleiben. Mara und Lena laufen und liegen in OfficeStory.
 */

const FLOOR = { w: 26, d: 22 }
const WALL_H = 6.5

// Positionen in Welteinheiten, auch von OfficeStory genutzt
export const SPOTS = {
  door: { x: 22.2, y: 0.3 },
  machine: { x: 3.5, y: 2.5, w: 3, d: 3, h: 3 },
  hrDesk: { x: 9.5, y: 7.6 },
  mara: { x: 10.9, y: 9.9 },
  table: { x: 11.2, y: 17 },
  maraTalk: { x: 10.2, y: 19.4 },
  lenaTalk: { x: 13.9, y: 18.6 },
  lenaSeat: { x: 21.9, y: 16.6 },
  monitor: { x: 10.9, y: 7.75, z: 1.4 },
  board: { x: 8.5, w: 7, z0: 2.3, z1: 5.7 }
}

// Wer wann lächelt (Bereiche von --l)
export const PEOPLE = [
  {
    id: 'vLead',
    x: 4.4,
    y: 12.6,
    body: '#4461D3',
    skin: '#C68B59',
    hair: '#2B1D14',
    happy: inB('analyse', 0.76, 0.84),
    label: 'Vertrieb'
  },
  {
    id: 'vMate',
    x: 4.4,
    y: 16.6,
    body: '#8FA4D8',
    skin: '#F1D3B3',
    hair: '#E6C27A',
    happy: inB('finale', 0.05, 0.2)
  },
  {
    id: 'csLead',
    x: 18.3,
    y: 11.9,
    body: 'green',
    skin: '#8D5524',
    hair: INK,
    happy: inB('analyse', 0.72, 0.8),
    label: 'CS Lead'
  },
  {
    id: 'csMate',
    x: 21.8,
    y: 11.9,
    body: 'yellow',
    skin: '#F2C9A5',
    hair: '#7A4A2A',
    happy: inB('analyse', 0.8, 0.88)
  },
  {
    id: 'csNew',
    x: 18.3,
    y: 16.4,
    body: '#546ED4',
    skin: '#E0AC69',
    hair: '#2B1D14',
    happy: inB('team', 0.62, 0.74)
  }
]

export const DESKS = [
  { x: 9.5, y: 7.6 },
  { x: 2, y: 11, w: 1.3, d: 2.2 },
  { x: 2, y: 15, w: 1.3, d: 2.2 },
  { x: 17.2, y: 9.6, w: 2.2, d: 1.2 },
  { x: 20.7, y: 9.6, w: 2.2, d: 1.2 },
  { x: 17.2, y: 14.1, w: 2.2, d: 1.2 },
  { x: 20.7, y: 14.1, w: 2.2, d: 1.2 }
]

// Auf den Boden gemalte Schilder
const FLOOR_LABELS = [
  { text: 'Vertrieb', x: 0.9, y: 19.2, size: 30 },
  { text: 'Customer Success', x: 16.9, y: 8.0, size: 30 },
  { text: 'Lounge', x: 20.6, y: 20.9, size: 30 },
  { text: 'HR', x: 8.7, y: 11.2, size: 24 }
]

export const PLANTS = [
  { x: 24.6, y: 0.5 },
  { x: 0.6, y: 20.8 },
  { x: 15.8, y: 20.6 },
  { x: 25, y: 12 }
]

export function Floor() {
  const { w, d } = FLOOR
  const lines = []
  for (let i = 2; i < w; i += 2) lines.push([iso(i, 0), iso(i, d)])
  for (let j = 2; j < d; j += 2) lines.push([iso(0, j), iso(w, j)])
  return (
    <g>
      <IsoBox
        x={0}
        y={0}
        z={-0.8}
        w={w}
        d={d}
        h={0.8}
        top='#F3F5FA'
        left='#C3CDE3'
        right='#AFBBD6'
      />
      {lines.map(([a, b], i) => (
        <line
          key={i}
          x1={a.x}
          y1={a.y}
          x2={b.x}
          y2={b.y}
          stroke='#E4E9F3'
          strokeWidth='1.2'
        />
      ))}
    </g>
  )
}

export function Walls() {
  const { w, d } = FLOOR
  const h = WALL_H
  const t = 0.4
  return (
    <g>
      {/* Wand B (links, x = 0) */}
      <polygon
        points={pts([iso(0, 0, 0), iso(0, d, 0), iso(0, d, h), iso(0, 0, h)])}
        fill='#EEF2FB'
      />
      <polygon
        points={pts([iso(-t, 0, h), iso(-t, d, h), iso(0, d, h), iso(0, 0, h)])}
        fill='#C9D2E6'
      />
      <polygon
        points={pts([iso(-t, d, 0), iso(0, d, 0), iso(0, d, h), iso(-t, d, h)])}
        fill='#D3DBEC'
      />
      {/* Wand A (rechts, y = 0) */}
      <polygon
        points={pts([iso(0, 0, 0), iso(w, 0, 0), iso(w, 0, h), iso(0, 0, h)])}
        fill='#FFFFFF'
      />
      <polygon
        points={pts([
          iso(-t, -t, h),
          iso(w, -t, h),
          iso(w, 0, h),
          iso(0, 0, h),
          iso(-t, 0, h)
        ])}
        fill='#C9D2E6'
      />
      <polygon
        points={pts([iso(w, -t, 0), iso(w, 0, 0), iso(w, 0, h), iso(w, -t, h)])}
        fill='#B7C3DD'
      />

      {/* Fenster auf Wand B */}
      {[6.5, 13.5].map((y0) => (
        <g key={y0}>
          <polygon
            points={pts([
              iso(0, y0, 2),
              iso(0, y0 + 3.6, 2),
              iso(0, y0 + 3.6, 5),
              iso(0, y0, 5)
            ])}
            fill='#CFE0FF'
          />
          <line
            {...seg2(iso(0, y0 + 1.8, 2), iso(0, y0 + 1.8, 5))}
            stroke='#FFFFFF'
            strokeWidth='3'
          />
          <line
            {...seg2(iso(0, y0, 3.5), iso(0, y0 + 3.6, 3.5))}
            stroke='#FFFFFF'
            strokeWidth='3'
          />
        </g>
      ))}
      {/* Aivy-Poster zwischen den Fenstern */}
      <polygon
        points={pts([
          iso(0, 10.9, 2.6),
          iso(0, 12.6, 2.6),
          iso(0, 12.6, 4.6),
          iso(0, 10.9, 4.6)
        ])}
        fill={COLORS.blue}
      />

      {/* Tür auf Wand A */}
      <polygon
        points={pts([
          iso(21, 0, 0),
          iso(23.4, 0, 0),
          iso(23.4, 0, 4.4),
          iso(21, 0, 4.4)
        ])}
        fill='#2B3654'
      />
      <polygon
        points={pts([
          iso(23.4, 0, 0),
          iso(23.4, 1.6, 0),
          iso(23.4, 1.6, 4.4),
          iso(23.4, 0, 4.4)
        ])}
        fill='#DCE3F2'
      />

      {/* Tafel für die Shortlist */}
      <polygon
        points={pts([
          iso(SPOTS.board.x - 0.2, 0, SPOTS.board.z0 - 0.2),
          iso(SPOTS.board.x + SPOTS.board.w + 0.2, 0, SPOTS.board.z0 - 0.2),
          iso(SPOTS.board.x + SPOTS.board.w + 0.2, 0, SPOTS.board.z1 + 0.2),
          iso(SPOTS.board.x - 0.2, 0, SPOTS.board.z1 + 0.2)
        ])}
        fill={INK}
      />
    </g>
  )
}

const seg2 = (a, b) => ({ x1: a.x, y1: a.y, x2: b.x, y2: b.y })

// Beim Blick in Aivy wird der Würfel zum Schnittmodell
export const MACHINE_GLASS = {
  ...pv('m', inB('magic', 0.04, 0.16)),
  ...pv('n', inB('magic', 0.9, 1)),
  opacity: 'calc(1 - 0.8 * var(--m) * (1 - var(--n)))'
}

export function Machine({ style = MACHINE_GLASS }) {
  const m = SPOTS.machine
  const top = iso(m.x + m.w / 2, m.y + m.d / 2, m.h)
  return (
    <g>
      <g style={style}>
        <IsoBox {...m} top='#6E9BFF' left={COLORS.blue} right='#2F62E0' />
      </g>
      <g transform={`translate(${top.x} ${top.y - 16})`}>
        <g className='off-spin'>
          <g transform='translate(-26 -10)'>
            <Shape kind='tri' color='yellow' size={20} />
          </g>
          <g transform='translate(-9 -10)'>
            <Shape kind='sq' color='green' size={18} />
          </g>
          <g transform='translate(8 -10)'>
            <Shape kind='dot' color='pink' size={18} />
          </g>
        </g>
      </g>
    </g>
  )
}

export function Lounge() {
  const sofa = { top: '#FFD3E2', left: '#F7B5CB', right: '#EFA0BB' }
  return (
    <g>
      <IsoBox
        x={21.8}
        y={17.6}
        w={1.4}
        d={0.9}
        h={0.45}
        top='#FFFFFF'
        left='#DDE3EF'
        right='#CBD3E3'
      />
      <IsoBox x={20.8} y={19.7} w={3.6} d={0.35} h={1.1} {...sofa} />
      <IsoBox x={20.8} y={20.05} w={3.6} d={1} h={0.5} {...sofa} />
    </g>
  )
}

export function Chair({ x, y }) {
  return (
    <g>
      <IsoBox
        x={x}
        y={y}
        w={0.9}
        d={0.9}
        h={0.5}
        top='#FFD3E2'
        left='#F7B5CB'
        right='#EFA0BB'
      />
      <IsoBox
        x={x}
        y={y}
        z={0.5}
        w={0.9}
        d={0.15}
        h={0.8}
        top='#FFD3E2'
        left='#F7B5CB'
        right='#EFA0BB'
      />
    </g>
  )
}

Chair.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired
}

/** Auf den Boden gemalte Schilder, unter den Möbeln */
export function FloorLabels({ labels }) {
  return (
    <g>
      {labels.map((f) => {
        const p = iso(f.x, f.y)
        return (
          <text
            key={f.text}
            transform={`matrix(0.866 0.5 -0.866 0.5 ${p.x} ${p.y})`}
            y={f.size}
            fontSize={f.size}
            className='off-floor-text'
          >
            {f.text}
          </text>
        )
      })}
    </g>
  )
}

FloorLabels.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.object).isRequired
}

Machine.propTypes = { style: PropTypes.object }

export function OfficeWorld() {
  const items = [
    ...DESKS.map((d) => ({
      depth: d.x + (d.w || 2.4) + d.y + (d.d || 1.3),
      node: <Desk {...d} />
    })),
    ...PLANTS.map((p) => ({ depth: p.x + p.y + 1.6, node: <Plant {...p} /> })),
    ...PEOPLE.map((p) => {
      const s = iso(p.x, p.y)
      return {
        depth: p.x + p.y + 0.4,
        node: (
          <g transform={`translate(${s.x} ${s.y})`}>
            <Person {...p} />
          </g>
        )
      }
    }),
    { depth: 12, node: <Machine /> },
    {
      depth: 31.4,
      node: (
        <IsoBox
          x={SPOTS.table.x}
          y={SPOTS.table.y}
          w={1.6}
          d={1.6}
          h={0.9}
          top='#FFFFFF'
          left='#DDE3EF'
          right='#CBD3E3'
        />
      )
    },
    { depth: 37.6, node: <Chair x={21.4} y={15.5} /> },
    { depth: 41, node: <Lounge /> }
  ].sort((a, b) => a.depth - b.depth)

  return (
    <g>
      <Floor />
      <Walls />
      <IsoRect x={8.5} y={15.2} w={7} d={5.6} fill='#FFF3D6' />
      <IsoRect x={20} y={17.4} w={6} d={4.6} fill='#FDE6EF' />
      <IsoRect x={16.6} y={8.8} w={7} d={8.6} fill='#E4F8F3' />
      <FloorLabels labels={FLOOR_LABELS} />
      {items.map((it, i) => (
        <g key={i}>{it.node}</g>
      ))}
      {PEOPLE.map((p) => {
        const s = iso(p.x, p.y)
        return (
          <Heart
            key={p.id}
            x={s.x}
            y={s.y - 78}
            at={inB('finale', 0.15, 0.32)}
          />
        )
      })}
    </g>
  )
}
